import {
  CustomPaging,
  GridColumnExtension,
  IntegratedPaging,
  PagingState,
  Table as TableBase,
} from "@devexpress/dx-react-grid";
import {
  ColumnChooser,
  Grid,
  PagingPanel,
  Table,
  TableColumnVisibility,
  TableFixedColumns,
  TableHeaderRow,
  Toolbar,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import { faCircleNotch, faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import arrayMove from "array-move";
import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Accordion, Button, Card, CardDeck, Row as SimpleRow } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { defineMessages, FormattedMessage, IntlShape, useIntl } from "react-intl";
import { SizeMe } from "react-sizeme";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import SweetAlert from "sweetalert2";
import ContentWrapper from "../../../components/content-wrapper";
import { useLocaleProvider } from "../../../components/locale-provider";
import { SectionHeading, SectionSubHeading } from "../../../components/section-heading";
import { IAuthzAction } from "../../../lib/action-authorizer";
import filterPills, { FilterValues } from "../../../lib/filterPills";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { TableActionProps, TableRowActionProps } from "../actions";
import { Identifiable } from "../context";
import { useLoaderState } from "../hooks";
import { Message } from "../i18n";
import { Renderer } from "../renderers";
import { FilterWidgetProps } from "../widgets";

const Container = styled(ContentWrapper)<{
  odd: boolean;
  resultsMsg: string;
  addBottomMarginToColumnOverlay: boolean;
}>`
  display: flex;
  flex-flow: column nowrap;
  height: 106%;

  .dx-g-bs4-paging-panel {
    display: flex;
    flex-flow: row-reverse;
    justify-content: space-between;
  }

  .dx-g-bs4-paging-panel > nav {
    margin-right: auto;
    float: none !important;
  }

  .dx-g-bs4-paging-panel > div {
    display: flex !important;
    align-items: center;
  }

  .dx-g-bs4-paging-panel > div:first-child {
    padding-left: 20px;
    position: relative;
  }

  .dx-g-bs4-paging-panel > div:first-child:before {
    position: absolute;
    left: -150px;
    font-weight: 600;
    content: "${props => props.resultsMsg}";

  }

  .dx-g-bs4-table-head {
    font-size: 11px;
    color: ${({ theme }) => theme.regularTextColor};
    font-family: ${({ theme }) => theme.regularTextTypeface};
  }

  .alert {
    margin-bottom: 0;
  }

  .table-responsive {
    background-color: rgb(245, 247, 250);

    .table {
      td {
        font-size: 11px;
        word-break: break-word;
        padding: 0 5px;
        vertical-align: middle;
      }

      margin-bottom: 0;

      tr td:first-child,
      th:first-child {
        padding-left: 20px;
      }

      th > div > div {
        text-transform: uppercase;
      }

      th {
        padding: 5px;
        max-width: 100px;
        word-break: break-word;
        background-color: rgb(250, 251, 252);
      }
    }
  }

  .even {
    background-color: rgb(250, 251, 252) !important;
  }

  .odd {
    background-color: rgb(245, 247, 250) !important;
  }

  .dx-g-bs4-fixed-cell {
    border-left: 1px solid rgb(222, 226, 230);
    z-index: 0;
  }

  .dx-g-bs4-header-cell.dx-g-bs4-fixed-cell {
    background-color: rgb(250, 251, 252) !important
  }

  .dx-g-bs4-toolbar .popover-inner {
    margin-bottom: ${props => (props.addBottomMarginToColumnOverlay ? "80px" : "0")};
  }

}
`;

export const StyledTableRow = styled(SortableElement<TableBase.DataRowProps>(Table.Row))`
  height: 40px;
  background-color: ${props =>
    props.index % 2 === 0 ? "rgb(245, 247, 250)" : "rgb(250, 251, 252)"};
`;

const StyledTableBody = styled(SortableContainer(Table.TableBody))``;

const StyledCard = styled(Card)`
  border: 0;
  margin: 0 !important;
`;

const StyledSimpleRow = styled(SimpleRow)`
  margin: 5px;
`;

const CustomCardTitle = styled(Card.Title)`
  display: inline-block;
  cursor: pointer;
`;

const TitleChevronContainer = styled.span`
  display: flex;
  align-items: center;
  max-width: 90vw;

  & > span:nth-child(2) {
    margin-right: 15px;
  }
`;

const Chevron = styled.span<{ toggleClosed: boolean }>`
  margin-left: 10px;
  border: solid ${({ theme }) => theme.regularTextColor};
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 3px;
  transform: ${props => (props.toggleClosed ? "rotate(45deg)" : "rotate(-135deg)")};
`;

const StyledCardDeck = styled(CardDeck)`
  margin-left: -5px;
  margin-bottom: 10px;
`;

const ColumnsMessage = styled.h5`
  cursor: pointer;
  display: flex;
  align-items: center;
`;

export const ExportButton = styled(Button)`
  height: 33px;
  margin-right: 14px;
  margin-left: 10px;
`;

export const StyledReactTooltip = styled(ReactTooltip)`
  text-transform: none;
  border-radius: 5px;
`;

export type TableColumns<T> = {
  [P in keyof T]?: Renderer<T[P]> | TableColumnConfig<T[P]>;
};

export type TableFilters<T, F> = {
  [P in keyof T & keyof F]?: TableFilter<T[P], F[P]>;
};

/**
 * Represents a table filter.
 *
 * Filters may be sync or async. Sync filters will filter out data based on a filter function locally, without
 * re-fetching data.
 *
 * Async filters will not filter current data, but attempt to reload the data by passing the filter values
 * to the onGetAll callback.
 *
 * T is the type of the field on the object to be filtered
 * F is the type that the filter widget returns.
 *
 * While it will be common that theses types are the same, it may not be the case (e.g. a date range filter)
 */
export type TableFilter<T, F> = {
  filterFn: "async" | ((dataValue: T, filterValue: F, intl?: IntlShape) => boolean);
  widget: React.ComponentType<FilterWidgetProps<F>>;
  title?: Message;
  width?: number;
};

type ColumnConfig<T> = {
  renderer?: Renderer<T>;
  width?: number;
  button?: (props: any) => React.ReactNode;
  align?: "left" | "center" | "right" | undefined;
};

type TableColumnConfig<T> = ColumnConfig<T> & {
  children?: {
    [key: string]: ColumnConfig<T> & {
      renderer: Renderer<T>;
    };
  };
};

function isTableConfig<T>(
  config?: Renderer<T> | TableColumnConfig<T>
): config is TableColumnConfig<T> {
  const tableColumnConfig: TableColumnConfig<T> = config as TableColumnConfig<T>;
  return !!(tableColumnConfig.renderer || tableColumnConfig.children);
}

export interface ListMessages {
  error: Message;
  loading: Message;
  loadRetry: Message;
  loadError: Message;
  noData: Message;
  entityName: Message;
  tableActions: Message;
  reorderFailedTitle: Message;
  reorderFailedContent: Message;
  saveSuccess: Message;
  filtersTitle: Message;
  filter: Message;
  clearFilters: Message;
  export: Message;
  columns: Message;
  resultPerPage: Message;
  tableHeaders?: { [key: string]: Message };
  tooltipHeaders?: { [key: string]: Message };
  import: Message;
  batchCancellation: Message;
  bulkUpdate: Message;
}

export const defaultListMessages: ListMessages = defineMessages({
  error: { id: "default.error", defaultMessage: "An error occurred" },
  loading: { id: "default.loading", defaultMessage: "Loading..." },
  loadError: { id: "default.loadError", defaultMessage: "Load Error" },
  loadRetry: { id: "default.retry", defaultMessage: "Retry" },
  noData: { id: "default.noData", defaultMessage: "No Data" },
  entityName: { id: "default.entity", defaultMessage: "Entity" },
  tableActions: { id: "default.tableActions", defaultMessage: "Actions" },
  reorderFailedTitle: { id: "default.reorderFailedTitle", defaultMessage: "Reordering failed" },
  reorderFailedContent: {
    id: "default.reorderFailedContent",
    defaultMessage: "An error occurred while trying to reorder the elements.",
  },
  saveSuccess: { id: "default.saveSuccess", defaultMessage: "Saved successfully" },
  filtersTitle: { id: "default.filters", defaultMessage: "Filters" },
  filter: { id: "default.filter", defaultMessage: "Filter" },
  clearFilters: { id: "default.clearFilters", defaultMessage: "Filter" },
  export: { id: "default.export", defaultMessage: "Export" },
  columns: { id: "default.columns", defaultMessage: "Columns" },
  resultPerPage: { id: "default.resultPerPage", defaultMessage: "Results per page:" },
  import: { id: "default.import", defaultMessage: "Import" },
  batchCancellation: { id: "default.batchCancellation", defaultMessage: "BatchCancellation" },
  bulkUpdate: { id: "default.bulkUpdate", defaultMessage: "Bulk update" },
});

export interface CrudListProps<T extends Identifiable, F> {
  showSuccessMessage?: boolean;
  paging?: boolean;
  uncontrolledPaging?: boolean;
  columnVisibility?: boolean;
  columnVisibilityExtensions?: {
    columnName: string;
    togglingEnabled: boolean;
  }[];
  pageSizes?: number[];
  initialHiddenColumnNames?: string[];
  totalRowsCount?: number;
  currentPage?: number;
  defaultPageSize?: number;
  currentPageSize?: number;
  managerName?: string;
  customRow?: (props: T[] | undefined) => React.ComponentType<TableBase.DataRowProps>;
  setCurrentPageSize?: React.Dispatch<React.SetStateAction<number>>;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  actions?: (props: TableActionProps<T>) => React.ReactNode;
  rowActionsTooltip?: () => React.ReactNode;
  rowActions?: (props: TableRowActionProps<T>) => React.ReactNode;
  additionalHeaderContent?: any;
  additionalFooterContent?: any;
  CSVRenderers?: { [key: string]: (value: any, fields: any) => string };
  listHeight?: number | string;
  hideExportButton?: boolean;

  onGetAll?(props: {
    filterValues?: Partial<F> | FilterValues;
    currentPage?: number;
    pageSize?: number;
    setTotalRowsCount?: React.Dispatch<React.SetStateAction<number>>;
  }): Promise<T[] | undefined>;

  onReorder?(id: T["id"], afterId: T["id"] | undefined): Promise<void>;

  onBatchCancellation?: any;
  showBatchCancellation?: boolean;

  onExport?(props: {
    filterValues?: Partial<F>;
    currentPage?: number;
    pageSize?: number;
  }): Promise<void> | void;

  onExportText?: string;
  columns: TableColumns<T>;
  messages?: ListMessages;
  subHeader?: string;
  filters?: TableFilters<T, F>;
  forceLoad?: any;
  fixedActions?: boolean;
  nestedData?: T[];
  canBulkUpdate?: IAuthzAction;
  onBulkUpdate?: () => void;
  filtersClosedByDefault?: boolean;
  firstLoad?: boolean;
}

const getRowId = (row: any) => {
  return row.id;
};

export const Loader = ({ loadMessage }: { loadMessage: Message }) => (
  <td colSpan={2} style={{ padding: "50px 0px" }}>
    <div style={{ display: "inline-block", position: "sticky", left: "50%" }}>
      <big
        className="text-muted"
        style={{
          display: "inline-block",
          transform: "translateX(-50%)",
          textAlign: "center",
        }}>
        <FormattedMessage {...loadMessage} />
        <br />
        <FontAwesomeIcon icon={faSyncAlt} spin />
      </big>
    </div>
  </td>
);

const LoadError = ({
  retry,
  failMessage,
  retryMessage,
}: {
  retry: () => void;
  failMessage: Message;
  retryMessage: Message;
}) => (
  <td colSpan={2} style={{ padding: "50px 0px" }}>
    <div style={{ display: "inline-block", position: "sticky", left: "50%" }}>
      <big className="text-muted" style={{ display: "inline-block" }}>
        <FormattedMessage {...failMessage} />
      </big>
      <br />
      <Button style={{ display: "inline-block" }} size="sm" onClick={retry}>
        <FormattedMessage {...retryMessage} />
      </Button>
    </div>
  </td>
);

const NoData = ({ noDataMessage }: { noDataMessage: Message }) => (
  <td colSpan={2} style={{ padding: "40px 0px" }}>
    <div style={{ display: "inline-block", position: "sticky", left: "50%" }}>
      <big
        className="text-muted"
        style={{ display: "inline-block", transform: "translateX(-50%)" }}>
        <FormattedMessage {...noDataMessage} />
      </big>
    </div>
  </td>
);
export default function CrudList<T extends Identifiable, F = {}>({
  actions,
  rowActionsTooltip,
  rowActions,
  columns,
  onGetAll,
  onExport,
  onExportText,
  onReorder,
  showSuccessMessage,
  messages = defaultListMessages,
  subHeader,
  filters,
  paging,
  uncontrolledPaging,
  columnVisibility,
  columnVisibilityExtensions = [],
  initialHiddenColumnNames,
  pageSizes,
  defaultPageSize,
  managerName,
  customRow,
  additionalHeaderContent,
  additionalFooterContent,
  CSVRenderers,
  listHeight,
  forceLoad,
  onBatchCancellation,
  showBatchCancellation,
  fixedActions,
  nestedData,
  canBulkUpdate,
  onBulkUpdate,
  hideExportButton,
  filtersClosedByDefault = true,
  firstLoad = true,
}: CrudListProps<T, F>) {
  const intl = useIntl();
  const locale = useLocaleProvider();
  const [{ loading, loadError, data }, loaderDispatch] = useLoaderState<T[]>({
    loading: firstLoad,
  });
  const [{ loading: exportLoading }, exportLoaderDispatch] = useLoaderState<T[]>({
    loading: false,
  });
  const [reordering, setReordering] = useState(false);
  const [syncFilterValues, setSyncFilterValues] = useState<Partial<F>>({});
  const [asyncFilterValues, setAsyncFilterValues] = useState<Partial<F>>(() =>
    window.localStorage.getItem("asyncFilterValues") && managerName
      ? JSON.parse(window.localStorage.getItem("asyncFilterValues") as string)[managerName]
      : {}
  );
  const [tempAsyncFilterValues, setTempAsyncFilterValues] = useState<Partial<F>>(
    asyncFilterValues || {}
  );
  const [datePickerInFocus, setDatePickerInFocus] = useState();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [toggleClosed, setToggleClosed] = useState(filtersClosedByDefault);
  const [hiddenColumnNames, setHiddenColumnNames] = useState(() =>
    window.localStorage.getItem("hiddenColumnNames")
      ? JSON.parse(window.localStorage.getItem("hiddenColumnNames") as string)
      : initialHiddenColumnNames
      ? initialHiddenColumnNames
      : []
  );
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  useEffect(() => {
    if (hiddenColumnNames) {
      window.localStorage.setItem("hiddenColumnNames", JSON.stringify(hiddenColumnNames));
      // load();
    }
    if (asyncFilterValues && managerName) {
      window.localStorage.setItem(
        "asyncFilterValues",
        JSON.stringify({
          ...(JSON.parse(window.localStorage.getItem("asyncFilterValues") as string) || {}),
          [managerName]: asyncFilterValues,
        })
      );
    }
  }, [hiddenColumnNames, asyncFilterValues, managerName]);

  const columnExtensions = useMemo(
    () =>
      Object.keys(columns)
        .reduce((r, k) => {
          const config = columns[k as keyof T];
          if (isTableConfig(config) && config.children) {
            const children = Object.keys(config.children).map(column => {
              const width = (config.children && config.children[column].width) || undefined;
              const align = (config.children && config.children[column].align) || undefined;
              return {
                columnName: `${k}.${column}`,
                width,
                align,
              };
            });
            r = [...r, ...children];
          } else {
            const width = isTableConfig(config) ? config.width : undefined;
            const align = isTableConfig(config) ? config.align : undefined;
            r.push({
              columnName: k,
              width,
              align,
            });
          }
          return r;
        }, [] as Array<GridColumnExtension>)
        .concat({ columnName: "__actions", width: "290", align: "left" }),
    [columns]
  );

  const applySyncFilters = useCallback(
    (data: T[]) => {
      if (!filters) return data;
      const filtersToApply = Object.keys(filters)
        .map(k => {
          const key = k as keyof T & keyof F;
          const filter = filters[key] as TableFilter<any, any> | undefined;
          return { key, filter };
        })
        .filter(
          ({ key, filter }) =>
            filter !== undefined &&
            filter.filterFn !== "async" &&
            syncFilterValues[key] !== undefined
        );

      return data.filter(value =>
        filtersToApply.reduce<boolean>((prev, next) => {
          const filterFn = next.filter?.filterFn;
          return (
            prev &&
            typeof filterFn === "function" &&
            filterFn(value[next.key], syncFilterValues[next.key], intl)
          );
        }, true)
      );
    },
    [syncFilterValues, filters, intl]
  );

  const getFilterValues = useCallback(
    (filterValues?: Partial<F>) => {
      if (!filters) return;
      return filterValues ?? asyncFilterValues;
    },
    [filters, asyncFilterValues]
  );

  const load = useCallback(
    async (props?: { page?: number; pageSize?: number; filters?: Partial<F> }) => {
      loaderDispatch({ type: "load_start" });
      try {
        const finalPage = props?.page ?? currentPage;
        const finalPageSize = props?.pageSize ?? pageSize;
        let filterValues = getFilterValues(props?.filters);
        const data = onGetAll
          ? await onGetAll({
              filterValues,
              currentPage: finalPage,
              pageSize: finalPageSize,
              setTotalRowsCount,
            })
          : nestedData;
        if (data) {
          loaderDispatch({ type: "load_success", data: data });
        }
      } catch (e) {
        console.error(e);
        loaderDispatch({
          type: "load_error",
          errorMessageId: "serviceError",
        });
      }
    },
    // forceLoad is used to reload the list from parent component
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onGetAll,
      loaderDispatch,
      filters,
      asyncFilterValues,
      currentPage,
      pageSize,
      setTotalRowsCount,
      forceLoad,
    ]
  );

  useEffect(() => {
    if (!firstLoad) return;
    load();
  }, [load, firstLoad]);

  useEffect(() => {
    if (typeof forceLoad === "undefined") return;
    load();
  }, [load, forceLoad]);

  const reload = useCallback(() => {
    load();
  }, [load]);

  const NoDataCell = useMemo(() => {
    return (props: Table.NoDataCellProps) =>
      loading ? (
        <Loader loadMessage={messages.loading} />
      ) : loadError ? (
        <LoadError
          retry={reload}
          failMessage={messages.loadError}
          retryMessage={messages.loadRetry}
        />
      ) : (
        <NoData noDataMessage={messages.noData} />
      );
  }, [loading, loadError, messages, reload]);

  const CellRenderer = useMemo(
    () => (props: any) => {
      const columnWidth =
        props.tableColumn.width ||
        document.getElementById(`column-id-${props.column.name}`)?.offsetWidth;

      const columnAlign = props.tableColumn.align;

      const rowType =
        Array.isArray(data) && data.findIndex(it => it.id === props.row.id) % 2 === 0
          ? "odd"
          : "even";

      if (props.column.name === "__actions") {
        return (
          <>
            {rowActions &&
              rowActions({
                rows: data,
                row: data ? data.find(row => row.id === props.row.id) : props.row,
                onReloadList: load,
                rest: {
                  ...props,
                  className: `${props.className} ${rowType}`,
                },
              })}
          </>
        );
      }
      const [field, childField] = props.column.name.split(".");
      const config = columns[field as keyof T] as Renderer<unknown> | TableColumnConfig<unknown>;
      const button = (config as ColumnConfig<unknown>).button;
      let R = config && isTableConfig(config) ? config.renderer : config;
      if (isTableConfig(config) && childField && config.children) {
        R = config.children[childField].renderer;
      }
      if (!R) {
        return <td />;
      }
      return (
        <td style={{ width: columnWidth, textAlign: columnAlign }}>
          <R value={props.value} button={button} rows={props.row} />
        </td>
      );
    },
    [rowActions, columns, load, data]
  );

  const tableColumns = useMemo(() => {
    const objectColumns = Object.keys(columns).reduce((results, field) => {
      const column = columns[field as keyof T];
      if (isTableConfig(column) && column.children) {
        const children = Object.keys(column.children).map(childField => ({
          name: `${field}.${childField}`,
          title: messages?.tableHeaders?.[childField]
            ? intl.formatMessage(messages?.tableHeaders[childField])
            : childField,
        }));
        results = [...results, ...children];
      } else {
        results.push({
          name: field,
          title: messages?.tableHeaders?.[field]
            ? intl.formatMessage(messages?.tableHeaders[field])
            : field,
        });
      }
      return results;
    }, [] as Array<any>);

    if (rowActions) {
      objectColumns.push({
        name: "__actions",
        title: intl.formatMessage(messages?.tableActions),
      });
    }
    return objectColumns;
  }, [columns, intl, rowActions, messages]);

  const rowData = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.map(d => {
      const dataByColumns = tableColumns.reduce(
        (r, { name: columnName }) => {
          const [field] = columnName.split(".");
          return {
            ...r,
            [columnName]: d[field as keyof T],
          };
        },
        { ...d }
      );
      return dataByColumns;
    });
  }, [data, tableColumns]);

  const filteredData = useMemo(
    () => (rowData === undefined ? undefined : applySyncFilters(rowData)),
    [applySyncFilters, rowData]
  );
  const rows = useMemo(() => filteredData || [], [filteredData]);

  const handleReorder = useCallback(
    async ({ oldIndex, newIndex }) => {
      //Don't call while a call is in progress;
      if (reordering) return;

      const oldRows = rows;
      loaderDispatch({
        type: "load_success",
        data: arrayMove(rows, oldIndex, newIndex),
      });
      if (onReorder) {
        setReordering(true);
        if (oldIndex <= newIndex) {
          newIndex = newIndex + 1;
        }
        const entityId = rows[oldIndex].id;
        const afterId = newIndex > 0 ? rows[newIndex - 1].id : undefined;
        try {
          await onReorder(entityId, afterId);
        } catch (e) {
          console.log("Error while reordering", e);
          loaderDispatch({ type: "load_success", data: oldRows });
          await SweetAlert.fire({
            icon: "error",
            title: intl.formatMessage(messages?.reorderFailedTitle),
            text: intl.formatMessage(messages?.reorderFailedContent),
          });
        } finally {
          setReordering(false);
        }
      }
    },
    [rows, onReorder, setReordering, reordering, loaderDispatch, intl, messages]
  );

  const Body = useMemo(
    () => ({ row, ...restProps }: any) => {
      return <StyledTableBody {...restProps} onSortEnd={handleReorder} useDragHandle />;
    },
    [handleReorder]
  );

  const Row = useMemo(
    () => ({ row, ...restProps }: any) => {
      return <StyledTableRow {...restProps} index={rows.indexOf(row)} />;
    },
    [rows]
  );

  const actionsNode = useMemo(() => actions && actions({ onReloadList: load }), [actions, load]);
  const rowActionsTooltipNode = useMemo(() => rowActionsTooltip && rowActionsTooltip(), [
    rowActionsTooltip,
  ]);

  const handleSetAsyncValues = useCallback(() => {
    setAsyncFilterValues(tempAsyncFilterValues);
    setCurrentPage(0);
    load({ page: 0, filters: tempAsyncFilterValues });
  }, [setAsyncFilterValues, tempAsyncFilterValues, load]);

  const handleClearFilters = useCallback(() => {
    setTempAsyncFilterValues({});
    setAsyncFilterValues({});
    setSyncFilterValues({});
    load({ page: 0, filters: {} });
  }, [setAsyncFilterValues, setTempAsyncFilterValues, load]);

  const handleFocusChange = useCallback(inFocus => {
    setDatePickerInFocus(inFocus);
  }, []);

  const handleCurrentPageChange = useCallback(
    page => {
      setCurrentPage(page);
      load({ page });
    },
    [setCurrentPage, load]
  );

  const handlePageSizeChange = useCallback(
    size => {
      setPageSize(size);
      setCurrentPage(0);
      load({ page: 0, pageSize: size });
    },
    [setPageSize, load]
  );

  const handleToggleClick = useCallback(
    event => {
      setToggleClosed(!toggleClosed);
    },
    [toggleClosed]
  );

  const toggleButtonComponent = useCallback(
    props => (
      <ColumnsMessage onClick={props.onToggle} ref={props.buttonRef}>
        <FormattedMessage {...messages.columns} />
        <Chevron toggleClosed={true} />
      </ColumnsMessage>
    ),
    [messages]
  );

  const customRowsMemo = useMemo(() => {
    if (typeof customRow === "function" && rows) {
      return customRow(rows);
    }
  }, [customRow, rows]);

  const filterLabels = useMemo(
    () =>
      messages?.tableHeaders &&
      Object.keys(messages?.tableHeaders).reduce((accum, curr) => {
        const obj = messages?.tableHeaders
          ? { [curr]: intl.formatMessage(messages?.tableHeaders[curr]) }
          : {};
        return { ...accum, ...obj };
      }, {}),
    [messages, intl]
  );

  const handleChangePills = useCallback(
    (filterValues?: Partial<F>) => {
      setAsyncFilterValues(filterValues || {});
      load({ filters: filterValues });
    },
    [load]
  );

  const pills = useMemo(
    () =>
      //@ts-ignore
      filterPills(asyncFilterValues, filterLabels, handleChangePills, setTempAsyncFilterValues),
    [asyncFilterValues, filterLabels, handleChangePills, setTempAsyncFilterValues]
  );

  const CSVReadyData = useMemo(() => {
    let columnsKeys = Object.keys(columns) || [];
    Object.values(columns).forEach(it => {
      if (typeof it.children === "object") {
        const childrenKeys = Object.keys(it.children);
        columnsKeys.push(...childrenKeys);
      }
    });
    const orderedFilteredKeys = columnsKeys.filter(it => !hiddenColumnNames.includes(it));

    return data
      ? data.map(entry =>
          orderedFilteredKeys.reduce<{}>((obj: { [key: string]: any }, key: keyof T & string) => {
            if (messages?.tableHeaders && messages?.tableHeaders[key]) {
              if (key === "translationsList") {
                const shortLocale = locale?.currentLocale?.substring(0, 2);
                const currentTranslations =
                  entry[key].find((it: any) => it.locale === shortLocale) || entry[key][0];

                Object.entries(currentTranslations).forEach(ent => {
                  if (messages?.tableHeaders && messages?.tableHeaders[ent[0]]) {
                    obj[intl.formatMessage(messages?.tableHeaders[ent[0]])] = ent[1];
                  }
                });
              } else if (
                typeof CSVRenderers === "object" &&
                Object.keys(CSVRenderers).includes(key) &&
                typeof CSVRenderers[key] === "function"
              ) {
                obj[intl.formatMessage(messages?.tableHeaders[key])] = CSVRenderers[key](
                  entry[key],
                  entry
                );
              } else {
                obj[intl.formatMessage(messages?.tableHeaders[key])] = entry[key];
              }
            }
            return obj;
          }, {})
        )
      : [];
  }, [data, hiddenColumnNames, messages, intl, columns, CSVRenderers, locale]);

  const hasAsyncFilters = useMemo(() => {
    if (filters) {
      return Object.values(filters).some((it: any) => it.filterFn === "async");
    }
  }, [filters]);

  const handleExport = useCallback(async () => {
    if (!onExport) {
      return;
    }
    let filterValues = filters ? asyncFilterValues : undefined;
    exportLoaderDispatch({ type: "load_start" });
    try {
      await onExport({ filterValues, currentPage, pageSize });
      exportLoaderDispatch({ type: "load_success", data: [] });
    } catch (e) {
      exportLoaderDispatch({
        type: "load_error",
        errorMessageId: "serviceError",
      });
      alert(intl.formatMessage(messages?.loadError));
      console.debug(e);
    }
  }, [
    onExport,
    filters,
    asyncFilterValues,
    exportLoaderDispatch,
    currentPage,
    pageSize,
    intl,
    messages,
  ]);

  // TODO: uncomment when back-end is implemented
  const showBulkUpdate = useMemo(
    () => false,
    []
    // onBulkUpdate && canBulkUpdate && authorizer.canExecute(canBulkUpdate),
    // [canBulkUpdate, onBulkUpdate]
  );

  const addBottomMarginToColumnOverlay = useMemo(
    () => (columns ? Object.keys(columns).length > 20 : false),
    [columns]
  );

  return (
    <Container
      odd={rows.length % 2 === 0}
      addBottomMarginToColumnOverlay={addBottomMarginToColumnOverlay}
      resultsMsg={intl.formatMessage(messages?.resultPerPage)}>
      <SectionHeading>
        <FormattedMessage {...messages.entityName} />
      </SectionHeading>
      {subHeader && <SectionSubHeading>{subHeader}</SectionSubHeading>}
      {showSuccessMessage && (
        <div role="alert" className="alert alert-success">
          <strong>
            <FormattedMessage {...messages.saveSuccess} />
          </strong>
        </div>
      )}
      {filters && (
        <Accordion defaultActiveKey={filtersClosedByDefault ? undefined : "0"}>
          <Card>
            <Card.Body>
              <Accordion.Toggle
                as={CustomCardTitle}
                variant="link"
                eventKey="0"
                onClick={handleToggleClick}>
                <TitleChevronContainer>
                  <span>
                    <FormattedMessage {...messages.filtersTitle} />
                  </span>
                  <Chevron toggleClosed={toggleClosed} />
                  {toggleClosed && pills}
                </TitleChevronContainer>
              </Accordion.Toggle>
              <Accordion.Collapse eventKey="0" className="!visible">
                <>
                  <StyledCardDeck>
                    {Object.keys(filters).map(k => {
                      const key = k as keyof F & keyof T & string;
                      const filter = filters[key] as TableFilter<any, any>;
                      const Widget: React.ComponentType<FilterWidgetProps<unknown>> = filter.widget;
                      const async = filter.filterFn === "async";
                      const updater = async ? setTempAsyncFilterValues : setSyncFilterValues;
                      return (
                        <StyledSimpleRow key={key}>
                          <StyledCard>
                            <Widget
                              value={
                                async ? tempAsyncFilterValues[key] || "" : syncFilterValues[key]
                              }
                              onChange={newValue =>
                                updater(prev => ({
                                  ...prev,
                                  [key]: newValue,
                                }))
                              }
                              placeholder={
                                (filter.title && intl.formatMessage(filter.title)) ||
                                tableColumns.find(n => n.name === key)?.title ||
                                ""
                              }
                              onFocusChange={handleFocusChange}
                              focused={datePickerInFocus}
                              width={filter.width}
                            />
                          </StyledCard>
                        </StyledSimpleRow>
                      );
                    })}
                  </StyledCardDeck>
                  {hasAsyncFilters && (
                    <>
                      <Button variant="primary" onClick={handleSetAsyncValues}>
                        <FormattedMessage {...messages.filter} />
                      </Button>{" "}
                    </>
                  )}
                  <Button variant="primary" onClick={handleClearFilters}>
                    <FormattedMessage {...messages.clearFilters} />
                  </Button>
                </>
              </Accordion.Collapse>
            </Card.Body>
          </Card>
        </Accordion>
      )}

      <div style={{ marginBottom: 20 }} />
      <div
        className="ml-3"
        style={{
          marginBottom: "15px",
          display: "flex",
          justifyContent: "space-between",
        }}>
        <span style={{ display: "flex" }}>
          <div>{actionsNode}</div>
          {/*TODO by default it shouldn't show the export button without the need for hideExportButton*/}
          {!hideExportButton &&
            !nestedData &&
            (onExport ? (
              <ExportButton disabled={exportLoading} onClick={handleExport} variant="primary">
                {exportLoading ? (
                  <FormattedMessage {...messages?.loading} />
                ) : onExportText ? (
                  onExportText
                ) : (
                  <FormattedMessage {...messages.export} />
                )}
              </ExportButton>
            ) : (
              <CSVLink
                style={{ marginLeft: "10px", marginRight: "18px" }}
                data={CSVReadyData}
                filename={`${managerName}_export_${format(new Date(), "yyyyMMdd")}.csv`}>
                <Button variant="primary">
                  <FormattedMessage {...messages.export} />
                </Button>
              </CSVLink>
            ))}

          {showBulkUpdate && (
            <ExportButton onClick={onBulkUpdate} variant="primary">
              <FormattedMessage {...messages.bulkUpdate} />
            </ExportButton>
          )}

          {showBatchCancellation && (
            <ExportButton disabled={exportLoading} onClick={onBatchCancellation} variant="primary">
              <FormattedMessage {...messages.batchCancellation} />
            </ExportButton>
          )}

          {reordering && (
            <div style={{ marginLeft: "auto", alignSelf: "center" }}>
              <FontAwesomeIcon icon={faCircleNotch} spin />
            </div>
          )}
        </span>
        {additionalHeaderContent && (
          <div style={{ marginLeft: "20px", marginRight: "20px", overflow: "hidden" }}>
            {additionalHeaderContent}
          </div>
        )}
      </div>

      <SizeMe monitorHeight monitorWidth={false} noPlaceholder>
        {({ size }) => (
          <div style={{ flexGrow: 1 }} className="ml-3 mr-3">
            <Grid rows={rows} getRowId={getRowId} columns={tableColumns}>
              {paging && (
                <PagingState
                  currentPage={currentPage}
                  pageSize={pageSize}
                  onCurrentPageChange={handleCurrentPageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
              {paging && <CustomPaging totalCount={totalRowsCount} />}
              {uncontrolledPaging && (
                <PagingState
                  defaultCurrentPage={0}
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
              {uncontrolledPaging && <IntegratedPaging />}
              <VirtualTable
                columnExtensions={columnExtensions}
                height={listHeight}
                noDataCellComponent={NoDataCell}
                cellComponent={CellRenderer}
                bodyComponent={Body}
                rowComponent={customRowsMemo || Row}
                estimatedRowHeight={53}
              />
              <TableHeaderRow
                // if it is not done here, the tooltips do not work
                contentComponent={({ column, children, ...restProps }: any) => (
                  <TableHeaderRow.Content
                    {...restProps}
                    id={`column-id-${column.name}`}
                    data-tip="React-tooltip"
                    data-for={`tooltip-${column.name}`}>
                    {messages?.tableHeaders && messages?.tableHeaders[column.name] && (
                      <StyledReactTooltip id={`tooltip-${column.name}`} effect="solid">
                        <FormattedMessage
                          {...(messages?.tooltipHeaders && messages?.tooltipHeaders[column.name]
                            ? messages?.tooltipHeaders[column.name]
                            : messages?.tableHeaders[column.name])}
                        />
                      </StyledReactTooltip>
                    )}
                    {column.name === "__actions" && rowActionsTooltipNode}
                    {children}
                  </TableHeaderRow.Content>
                )}
              />
              {fixedActions && <TableFixedColumns rightColumns={["__actions"]} />}
              {columnVisibility && (
                <TableColumnVisibility
                  hiddenColumnNames={hiddenColumnNames}
                  onHiddenColumnNamesChange={setHiddenColumnNames}
                  columnExtensions={[
                    { columnName: "__actions", togglingEnabled: false },
                    ...columnVisibilityExtensions,
                  ]}
                />
              )}
              {columnVisibility && <Toolbar />}
              {columnVisibility && <ColumnChooser toggleButtonComponent={toggleButtonComponent} />}

              {(paging || uncontrolledPaging) && <PagingPanel pageSizes={pageSizes} />}
            </Grid>
            {additionalFooterContent}
          </div>
        )}
      </SizeMe>
    </Container>
  );
}
